import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ProcessSection from './components/ProcessSection';
import QuoteSection from './components/QuoteSection';
import TestimonialSection from './components/TestimonialSection';
import ProductsSection from './components/ProductsSection';
import MapSection from './components/MapSection';
import Footer from './components/Footer';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ns-techlaser.com';

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ManufacturingCompany",
      "name": "NS-TECH",
      "description": "경기 포천 판금가공 전문기업. CAD 설계부터 레이저 가공, 절곡, v컷팅, 용접, 조립까지 전 공정 올인원 판금 제조. 대량 5천개 이상 납품 실적 보유.",
      "url": siteUrl,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "소흘로 11",
        "addressLocality": "포천시 소흘읍",
        "addressRegion": "경기도",
        "addressCountry": "KR"
      },
      "telephone": "031-541-1902",
      "email": "nstech01@naver.com",
      "openingHours": "Mo-Fr 08:00-18:00"
    },
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "NS-TECH 포천 판금가공",
      "description": "경기 포천 판금가공 전문 제조 기업",
      "url": siteUrl,
      "telephone": "031-541-1902",
      "email": "nstech01@naver.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "소흘로 11",
        "addressLocality": "포천시 소흘읍",
        "addressRegion": "경기도",
        "addressCountry": "KR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "37.8942",
        "longitude": "127.2003"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "NS-TECH",
      "url": siteUrl,
      "description": "판금가공 전문 제조 기업",
      "publisher": {
        "@type": "Organization",
        "name": "NS-TECH"
      }
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 모바일 메뉴 열릴 때 body 스크롤 잠금
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleQuoteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setShowContactModal(true);
  };

  const navLinks = [
    { href: '#about', label: '회사소개', type: 'dropdown', subMenu: [
      { href: '#about', label: '회사소개', isScroll: true },
      { href: '/history', label: '연혁', isScroll: false },
      { href: '/organization', label: '조직도', isScroll: false },
      { href: '#map', label: '오시는 길', isScroll: true },
    ]},
    { href: '#process', label: '생산공정', type: 'scroll' },
    { href: '#equipment', label: '보유장비', type: 'dropdown', subMenu: [
      { href: '/equipment/production', label: '생산장비' },
      { href: '/equipment/welding', label: '용접장비' },
      { href: '/equipment/capability', label: '가공능력' },
      { href: '/equipment/materials', label: '가공 가능자재' },
    ]},
    { href: '#products', label: '제조물품', type: 'dropdown', subMenu: [
      { href: '/gallery', label: '갤러리' },
    ]},
    { href: '#support', label: '고객지원', type: 'contact' },
    { href: '#quote', label: '견적문의', cta: true, type: 'scroll' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="경기 포천 판금가공 NS-TECH | 레이저가공 절곡 용접 V컷팅 조립"
        description="경기 포천 판금가공 전문기업 NS-TECH. 포천 레이저가공·절곡·v컷팅·용접·조립 올인원. 대량 5천개 이상 납품 실적, 대기업 협력사 공급망 경험 보유."
        keywords="포천판금가공, 포천레이저가공, 포천용접, 포천절곡, 포천v컷팅, 경기판금가공, 레이저가공, 절곡, v컷팅, 벤딩, 용접, 대량제작"
        canonical={siteUrl}
        ogTitle="경기 포천 판금가공 NS-TECH | 레이저가공 절곡 용접 V컷팅"
        ogDescription="경기 포천 판금가공 전문 NS-TECH. 레이저가공·절곡·v컷팅·용접 올인원. 대량 5천개 이상 납품 실적 보유."
        ogType="website"
        ogUrl={siteUrl}
        jsonLd={jsonLd}
      />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="w-full px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">

            {/* 좌측: 로고 + 모든 메뉴 (고객지원 제외) */}
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/" className="flex items-center gap-2 shrink-0 mr-2" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
                <img
                  src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                  alt="NS-TECH Logo"
                  className="h-12 w-auto object-contain"
                />
              </Link>

              {/* 일반 메뉴 */}
              {navLinks.filter(l => !l.cta && l.href !== '#support').map((link) => (
                link.type === 'dropdown' ? (
                  <div 
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => {
                      if (link.label === '보유장비') setEquipmentDropdownOpen(true);
                      if (link.label === '제조물품') setProductsDropdownOpen(true);
                      if (link.label === '회사소개') setAboutDropdownOpen(true);
                    }}
                    onMouseLeave={() => {
                      if (link.label === '보유장비') setEquipmentDropdownOpen(false);
                      if (link.label === '제조물품') setProductsDropdownOpen(false);
                      if (link.label === '회사소개') setAboutDropdownOpen(false);
                    }}
                  >
                    <button
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center gap-1 cursor-pointer"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line transition-transform ${
                        (link.label === '보유장비' && equipmentDropdownOpen) || 
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)
                          ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {((link.label === '보유장비' && equipmentDropdownOpen) || 
                      (link.label === '제조물품' && productsDropdownOpen) ||
                      (link.label === '회사소개' && aboutDropdownOpen)) && (
                      <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                        {link.subMenu?.map((subLink) => (
                          subLink.isScroll ? (
                            <a
                              key={subLink.href}
                              href={subLink.href}
                              onClick={(e) => { e.preventDefault(); setAboutDropdownOpen(false); handleNavClick(subLink.href); }}
                              className="block px-4 py-2 text-sm text-gray-700 hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              {subLink.label}
                            </a>
                          ) : (
                            <Link
                              key={subLink.href}
                              to={subLink.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap"
                            >
                              {subLink.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : link.type === 'link' ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </a>
                )
              ))}

              {/* 견적문의 강조 버튼 */}
              <button
                onClick={handleQuoteClick}
                className="text-sm font-bold transition-all whitespace-nowrap px-5 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 shadow-md cursor-pointer"
              >
                견적문의
              </button>
            </div>

            {/* 모바일: 로고 */}
            <div className="flex lg:hidden items-center">
              <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
                <img
                  src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                  alt="NS-TECH Logo"
                  className="h-11 w-auto object-contain"
                />
              </Link>
            </div>

            {/* 우측: 고객지원 (데스크탑) + 햄버거 (모바일) */}
            <div className="flex items-center gap-3">
              {/* 고객지원 - 데스크탑만 */}
              <button
                onClick={() => setShowContactModal(true)}
                className="hidden lg:block text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
              >
                고객지원
              </button>

              {/* 모바일 햄버거 버튼 */}
              <button
                className="lg:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="메뉴 열기"
              >
                <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </button>
            </div>
          </div>
        </div>

        {/* 모바일 배경 오버레이 */}
        <div 
          className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>

        {/* 모바일 드롭다운 메뉴 */}
        <div className={`lg:hidden fixed top-[64px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
          <div className="bg-white shadow-xl border-t border-gray-100 px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              link.type === 'dropdown' ? (
                <div key={link.href}>
                  <button
                    onClick={() => {
                      if (link.label === '보유장비') setMobileEquipmentOpen(!mobileEquipmentOpen);
                      if (link.label === '제조물품') setMobileProductsOpen(!mobileProductsOpen);
                      if (link.label === '회사소개') setMobileAboutOpen(!mobileAboutOpen);
                    }}
                    className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between"
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${
                      (link.label === '보유장비' && mobileEquipmentOpen) || 
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)
                        ? 'rotate-180' : ''
                    }`}></i>
                  </button>
                  {((link.label === '보유장비' && mobileEquipmentOpen) || 
                    (link.label === '제조물품' && mobileProductsOpen) ||
                    (link.label === '회사소개' && mobileAboutOpen)) && (
                    <div className="pl-4 mt-1 space-y-1">
                      {link.subMenu?.map((subLink) => (
                        subLink.isScroll ? (
                          <a
                            key={subLink.href}
                            href={subLink.href}
                            onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); handleNavClick(subLink.href); }}
                            className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                          >
                            {subLink.label}
                          </a>
                        ) : (
                          <Link
                            key={subLink.href}
                            to={subLink.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                          >
                            {subLink.label}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : link.type === 'contact' ? (
                <button
                  key={link.href}
                  onClick={() => { setMobileMenuOpen(false); setShowContactModal(true); }}
                  className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                >
                  {link.label}
                </button>
              ) : link.type === 'link' ? (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer block text-left ${
                    link.cta
                      ? 'bg-orange-500 text-white font-bold mt-2 text-center'
                      : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.href}
                  onClick={link.cta ? handleQuoteClick : (e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className={`w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left ${
                    link.cta
                      ? 'bg-orange-500 text-white font-bold mt-2 text-center'
                      : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {link.label}
                </button>
              )
            ))}
          </div>
        </div>
      </nav>

      <HeroSection />
      <AboutSection />
      <ProcessSection />
      <ProductsSection />
      <QuoteSection />
      <MapSection />
      <TestimonialSection />
      <Footer />

      {/* 모바일 플로팅 전화 버튼 */}
      <a
        href="tel:031-541-1902"
        className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
        aria-label="전화 연결"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-phone-fill text-white text-base"></i>
        </div>
        <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
      </a>

      {/* 연락처 모달 */}
      {showContactModal && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowContactModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="닫기"
            >
              <i className="ri-close-line text-gray-600 text-xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-orange-500 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">견적 문의</h3>
              <p className="text-sm text-gray-500">편하신 방법으로 연락주세요</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-building-line text-orange-500"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500">회사명</p>
                  <p className="text-base font-semibold text-gray-900">NS-TECH</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-phone-line text-orange-500"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">전화번호</p>
                  <p className="text-base font-semibold text-gray-900">031-541-1902</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-printer-line text-orange-500"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">팩스</p>
                  <p className="text-base font-semibold text-gray-900">031-541-1903</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-orange-500"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">이메일</p>
                  <p className="text-base font-semibold text-gray-900">nstech01@naver.com</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-orange-500"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">운영시간</p>
                  <p className="text-base font-semibold text-gray-900">AM 08:00 ~ PM 18:00</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <a
                href="tel:031-541-1902"
                className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                <span>전화하기</span>
              </a>
              <a
                href="mailto:nstech01@naver.com"
                className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                <span>메일보내기</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}